import { Injectable } from "@angular/core";
import mqtt from "mqtt";
import { EMPTY, Observable } from "rxjs";
import { environment } from "src/environments/environment";

@Injectable({
    providedIn: "root",
})
export class MqttService {
    private client: mqtt.MqttClient | null = null;
    private connectionUnavailable = false;
    private connectAttempted = false;

    subscribeToTopic(
        topic: string,
    ): Observable<{ topic: string; message: string }> {
        if (!this.ensureConnected()) {
            return EMPTY;
        }

        return new Observable((observer) => {
            const client = this.client;
            if (!client) {
                observer.complete();
                return;
            }

            const onMessage = (receivedTopic: string, message: Buffer) => {
                if (receivedTopic === topic) {
                    observer.next({
                        topic: receivedTopic,
                        message: message.toString(),
                    });
                }
            };

            client.subscribe(topic, (err) => {
                if (err) {
                    console.warn(`MQTT subscription failed for ${topic}:`, err);
                    observer.complete();
                } else {
                    console.info(`Subscribed to mqtt topic: ${topic}`);
                }
            });

            client.on("message", onMessage);

            return () => {
                client.off("message", onMessage);
            };
        });
    }

    isConnected(): boolean {
        return this.client?.connected ?? false;
    }

    private ensureConnected(): boolean {
        if (this.connectionUnavailable) {
            return false;
        }

        if (this.client) {
            return true;
        }

        if (this.connectAttempted) {
            return false;
        }

        this.connectAttempted = true;

        const mqttEnabled = environment.mqttEnabled ?? true;
        const mqttUrl = environment.mqttUrl?.trim() ?? "";

        if (!mqttEnabled || !mqttUrl) {
            this.connectionUnavailable = true;
            return false;
        }

        try {
            this.client = mqtt.connect(mqttUrl, {
                username: environment.mqttUser,
                password: environment.mqttPassword,
                clean: true,
                reconnectPeriod: 0,
            });

            this.client.on("error", (err) => {
                console.warn("MQTT client error:", err);
            });
        } catch (err) {
            console.warn("MQTT connection failed:", err);
            this.client = null;
            this.connectionUnavailable = true;
            return false;
        }

        return true;
    }
}
